import React, { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, RefreshCw } from 'lucide-react';
import api from '../../lib/api';

/**
 * OrgSwitcher — מעבר מהיר בין עמותות (פיצ'ר multiOrgAccess).
 *
 * מוצג רק כשלמשתמש יש גישה ליותר מעמותה אחת (הוענקה ע"י סופר-אדמין
 * ורק כשהפיצ'ר דלוק על עמותת היעד). לכל שאר המשתמשים — לא מרונדר כלום.
 *
 * מעבר = הנפקת JWT חדש בצד השרת ("כאילו התנתק והתחבר מחדש") + רענון מלא
 * של האפליקציה, כך שכל ה-state/cache של העמותה הקודמת מתאפס.
 */
interface MyOrg {
  _id: string;
  name: string;
  role: string;
  isHome: boolean;
}

const OrgSwitcher: React.FC = () => {
  const [orgs, setOrgs] = useState<MyOrg[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/auth/my-orgs')
      .then((res) => {
        if (cancelled) return;
        setOrgs(res.data?.orgs || []);
        setCurrentId(res.data?.current || null);
      })
      .catch(() => {
        /* אין גישה רב-עמותתית / שגיאה — פשוט לא מציגים switcher */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // עמותה אחת בלבד (המצב של כמעט כל המשתמשים) — אין מה להציג
  if (orgs.length <= 1) return null;

  const current = orgs.find((o) => o._id === currentId) || orgs[0];

  const switchTo = async (orgId: string) => {
    if (orgId === currentId || switching) return;
    setSwitching(orgId);
    try {
      const res = await api.post('/auth/switch-org', { orgId });
      const { token, user } = res.data || {};
      if (!token) throw new Error('no token');
      localStorage.setItem('token', token);
      if (user) localStorage.setItem('user', JSON.stringify(user));
      // רענון מלא — מאפס את כל ה-state של העמותה הקודמת (כמו לוגין חדש)
      window.location.href = '/';
    } catch (e: any) {
      setSwitching(null);
      alert(e?.response?.data?.error || 'המעבר נכשל, נסה שוב');
    }
  };

  return (
    <div className="relative flex-shrink-0" ref={ref} dir="rtl">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[#E0E0E0] dark:border-[#2C2C2C] bg-white dark:bg-[#1E1E1E] hover:bg-[#F5F5F5] dark:hover:bg-[#2C2C2C] text-sm font-semibold text-[#1C1B1F] dark:text-white transition-all duration-200 max-w-[220px]"
        aria-label="החלף עמותה"
        title="החלף עמותה"
      >
        <Building2 size={16} className="text-[#1565C0] dark:text-[#90CAF9] flex-shrink-0" />
        <span className="truncate">{current?.name || 'עמותה'}</span>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-64 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-xl border border-[#E0E0E0] dark:border-[#2C2C2C] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#F0F0F0] dark:border-[#2C2C2C] text-xs font-semibold text-[#9E9E9E]">
            מעבר בין עמותות
          </div>
          {orgs.map((o) => {
            const active = o._id === currentId;
            return (
              <button
                key={o._id}
                onClick={() => switchTo(o._id)}
                disabled={!!switching}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-right ${
                  active
                    ? 'bg-[#E3F2FD] dark:bg-[#0D47A1]/20 text-[#1565C0] dark:text-[#90CAF9] font-semibold'
                    : 'text-[#49454F] dark:text-[#CAC4D0] hover:bg-[#F5F5F5] dark:hover:bg-[#2C2C2C]'
                }`}
              >
                {switching === o._id ? (
                  <RefreshCw size={15} className="animate-spin flex-shrink-0" />
                ) : active ? (
                  <Check size={15} className="flex-shrink-0" />
                ) : (
                  <Building2 size={15} className="flex-shrink-0 opacity-50" />
                )}
                <span className="flex-1 truncate">{o.name}</span>
                {o.isHome && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-[#F5F5F5] dark:bg-[#2C2C2C] text-[#9E9E9E] flex-shrink-0">
                    בית
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
